import CampaignService from "services/Campaign";
import CampaignTargetCallService from "services/CampaignTargetCall";
import CampaignTargetEmailService from "services/CampaignTargetEmail";
import CaseService from "services/Case";
import ContactService from "services/Contact";
import CustomTableRowService from "services/CustomTableRow";
import DataJobService from "services/DataJob";
import EntityService from "services/Entity";
import MachineService from "services/Machine";
import RevenueOpportunityService from "services/RevenueOpportunities";
import UserService from "services/User";

// FE mirror of the backend relationship-target registry
// (api/main/services/dynamic_schema/relationship_targets.py). Each standard
// target maps to its detail route (null = label-only, no detail page) and a
// `query(params)` that wraps its for_filters service call. The record picker
// reads `fetchOptions`/`queryFn`, both derived from `query` so the params
// object is the single source of truth. Machine has no detail page in the
// router, so it stays label-only.
const CUSTOM_TABLE_TARGET_PREFIX = "CustomTable:";

const STANDARD_TARGETS = {
  Contact: {
    label: "Contact",
    detailRoute: id => `/contacts/${id}`,
    query: params => ContactService.getContactsForFilters(params),
  },
  Entity: {
    label: "Entity",
    detailRoute: id => `/entities/${id}`,
    query: params => EntityService.getEntitiesForFilters(params),
  },
  Campaign: {
    label: "Campaign",
    detailRoute: id => `/campaigns/${id}`,
    query: params => CampaignService.getCampaignsForFilters(params),
  },
  Case: {
    label: "Case",
    detailRoute: id => `/cases/${id}`,
    query: params => CaseService.getForFilters(params),
  },
  Machine: {
    label: "Machine",
    detailRoute: null,
    // The Machine FK targets the integer PK, but getForFilters defaults to the
    // machine_id string as the value for legacy table filters; value_by_pk
    // flips it to the PK value with machine_id as the label. The route marshals
    // id as a string (simple_string_object), so coerce back to the numeric FK
    // the relationship column's z.number() schema requires.
    query: async params => {
      const rows = await MachineService.getForFilters({
        ...params,
        value_by_pk: true,
      });
      return rows.map(r => ({ ...r, id: Number(r.id) }));
    },
  },
  RevenueOpportunity: {
    label: "Revenue Opportunity",
    detailRoute: id => `/revenue_opportunities/${id}`,
    query: params => RevenueOpportunityService.getForFilters(params),
  },
  User: {
    label: "User",
    detailRoute: null,
    query: params => UserService.getUsersForFilters(params),
  },
  CampaignTargetCall: {
    label: "Call",
    detailRoute: null,
    query: params => CampaignTargetCallService.getForFilters(params),
  },
  CampaignTargetEmail: {
    label: "Email",
    detailRoute: null,
    query: params => CampaignTargetEmailService.getForFilters(params),
  },
  DataJob: {
    label: "Data Job",
    detailRoute: null,
    query: params => DataJobService.getForFilters(params),
  },
};

// `query` takes a params object so both record-picker call shapes share one
// service call: `fetchOptions(search)` keeps the legacy string contract its
// callers expect, while `queryFn(filters)` forwards the whole RuvixxSelect
// filters object (search_query while typing, model_id to resolve a stored id).
const withPickerHandles = ({ query, ...target }) => ({
  ...target,
  fetchOptions: search => query({ search_query: search }),
  queryFn: filters => query(filters),
});

export const isCustomTableTarget = targetType =>
  typeof targetType === "string" &&
  targetType.startsWith(CUSTOM_TABLE_TARGET_PREFIX);

export const customTableTargetSlug = targetType =>
  isCustomTableTarget(targetType)
    ? targetType.slice(CUSTOM_TABLE_TARGET_PREFIX.length)
    : null;

export const customTableTargetType = slug =>
  `${CUSTOM_TABLE_TARGET_PREFIX}${slug}`;

/**
 * Resolve a column's `options.target_type` (+ optional `label_field_id`)
 * to `{ label, detailRoute, fetchOptions, queryFn }`. Returns null for
 * unknown target types so callers can degrade to a plain numeric cell.
 */
export const resolveRelationshipTarget = (targetType, options = {}) => {
  if (isCustomTableTarget(targetType)) {
    const slug = customTableTargetSlug(targetType);
    const labelField = options.label_field_id
      ? { label_field_id: options.label_field_id }
      : {};
    return withPickerHandles({
      label: slug,
      detailRoute: id => `/custom_tables/${slug}/${id}`,
      query: params =>
        CustomTableRowService.getRowsForFilters(slug, {
          ...params,
          ...labelField,
        }),
    });
  }
  const target = STANDARD_TARGETS[targetType];
  return target ? withPickerHandles(target) : null;
};

/** Options for the FieldDialog's standard-target dropdown. */
export const STANDARD_TARGET_OPTIONS = Object.entries(STANDARD_TARGETS).map(
  ([value, { label }]) => ({ value, label })
);

export default resolveRelationshipTarget;
